import moment from 'moment';

// NAS-HA-6110 — pure helpers for the Home Audio CS console. The list and the drawer both
// read from here so labels and request bodies cannot drift between them.

export const HA_PAGE_SIZE = 20;

// Bucket select value that means "unpaid / no bucket". Sent as unbucketed_only, never
// alongside bucket_id — the backend rejects that pair.
export const UNBUCKETED_VALUE = 'unbucketed';

const SOURCE_LABELS = {
    website_addon: 'Website — Add-on at Purchase',
    support_page: 'Website — Direct Booking',
    csv_bulk: 'Manual — Channel Upload',
};

export const titleCase = (value) =>
    String(value || '')
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

// Falls back to a readable form so a source added on the server still renders.
export const sourceLabel = (source) => SOURCE_LABELS[source] || titleCase(source) || '—';

// The three sources a CS agent can correct a row to (PRD §5.3). Only csv_bulk takes a channel.
export const SOURCE_OPTIONS = Object.keys(SOURCE_LABELS).map((value) => ({ value, label: SOURCE_LABELS[value] }));
export const SOURCE_WITH_CHANNEL = 'csv_bulk';

export const LOG_ONLY_ACTIONS = ['call', 'call_reminder'];

// The Complaints tab is the shared complaint board scoped to this product line. `scope` is
// sent on the bucket/status lists, `listFlag` on the complaint list; both are backend names.
export const HOME_AUDIO_BOARD = { scope: 'home_audio', listFlag: 'home_audio_flag' };

export const HOME_AUDIO_COMPLAINT_ACTIONS = [
    { key: 'installation_assigned_sp', label: 'Installation Assigned to Service Partner', icon: 'fas fa-tools' },
    { key: 'service_assigned_sp', label: 'Service Assigned to Service Partner', icon: 'fas fa-user-cog' },
    { key: 'parts_used', label: 'Parts Used', icon: 'fas fa-cogs' },
];

// History-only like a call, but never in allowed_actions: the Comments tab is always open.
export const COMMENT_ACTION = 'comment';

export const DETAIL_TABS = [
    { key: 'product', label: 'Product Details' },
    { key: 'customer', label: 'Customer Details' },
    { key: 'conversations', label: 'Conversations' },
    { key: 'comments', label: 'Comments' },
    { key: 'history', label: 'History' },
];

const CONVERSATION_ACTIONS = ['call', 'call_reminder', 'notification', 'sms', 'email'];

// Every history row lands in exactly one tab.
export const historyTab = (action) => {
    if (action === COMMENT_ACTION) return 'comments';
    if (CONVERSATION_ACTIONS.indexOf(action) > -1) return 'conversations';
    return 'history';
};

export const groupHistoryByTab = (history) => {
    const groups = { conversations: [], comments: [], history: [] };
    (Array.isArray(history) ? history : []).forEach((entry) => groups[historyTab(entry.action)].push(entry));
    return groups;
};

export const ACTION_LABELS = {
    invalid_invoice: 'Move to Invalid Invoice',
    payment_pending: 'Move to Payment Pending',
    payment_done: 'Mark Payment Done',
    assign: 'Assign to Service Partner',
    close: 'Close Request',
    revoke: 'Revoke Request',
    mark_duplicate: 'Mark as Duplicate',
    unmark_duplicate: 'Remove Duplicate Flag',
    call: 'Log Customer Call',
    call_reminder: 'Log Call Reminder',
    comment: 'Internal Comment',
    create: 'Request Created',
    payment: 'Payment',
    export: 'Exported to Jeeves',
    reschedule: 'Rescheduled by Customer',
    revoke_after_export: 'Revoked After Export',
    source_change: 'Source Corrected',
    payment_verify: 'Payment Verified with Cashfree',
};

export const actionLabel = (action) => ACTION_LABELS[action] || titleCase(action);

export const isLogOnlyAction = (action) => LOG_ONLY_ACTIONS.indexOf(action) > -1;

export const splitActions = (allowedActions) => {
    const allowed = Array.isArray(allowedActions) ? allowedActions : [];
    return {
        bucketActions: allowed.filter((action) => !isLogOnlyAction(action)),
        logActions: allowed.filter(isLogOnlyAction),
    };
};

export const buildListBody = (filters) => {
    const body = {
        page_no: filters.page || 1,
        limit: filters.limit || HA_PAGE_SIZE,
    };
    if (filters.bucket === UNBUCKETED_VALUE) {
        body.unbucketed_only = true;
    } else if (filters.bucket) {
        body.bucket_id = Number(filters.bucket);
    }
    if (filters.source) {
        body.source = filters.source;
    }
    if (filters.startDate) {
        body.startDate = filters.startDate;
    }
    if (filters.endDate) {
        body.endDate = filters.endDate;
    }
    const search = String(filters.search || '').trim();
    if (search) {
        body.search_by = search;
    }
    return body;
};

// Only the fields the chosen action actually uses; anything blank is left out so the
// server keeps its own defaults.
// Bulk offers only the moves that need no per-request input. Close is deliberately absent:
// a closure reason is a per-request judgement, not something to stamp across a selection.
export const BULK_ACTIONS = ['payment_done', 'payment_pending', 'invalid_invoice', 'assign', 'revoke'];

export const buildActionBody = (requestId, action, form) => {
    const values = form || {};
    const body = { request_id: Number(requestId), action };
    const comments = String(values.comments || '').trim();
    if (comments) {
        body.comments = comments;
    }
    if (action === 'close' && values.closure_reason_id) {
        body.closure_reason_id = Number(values.closure_reason_id);
    }
    if (action === 'assign' && String(values.scheduled_date || '').trim()) {
        body.scheduled_date = String(values.scheduled_date).trim();
    }
    if (action === 'mark_duplicate' && String(values.duplicate_of_request_id || '').trim()) {
        body.duplicate_of_request_id = Number(values.duplicate_of_request_id);
    }
    return body;
};

// Client-side guard only — the API validates these again.
export const validateActionForm = (action, form) => {
    const values = form || {};
    if (action === COMMENT_ACTION && !String(values.comments || '').trim()) {
        return 'Enter a comment first.';
    }
    if (action === 'close' && !values.closure_reason_id) {
        return 'Select a closure reason to close this request.';
    }
    if (action === 'mark_duplicate') {
        const duplicateOf = String(values.duplicate_of_request_id || '').trim();
        if (duplicateOf && !/^\d+$/.test(duplicateOf)) {
            return 'Duplicate of request must be a numeric request id.';
        }
    }
    return null;
};

export const formatDate = (value) => (value ? moment(value).format('DD MMM YYYY') : '—');

export const formatDateTime = (value) => (value ? moment(value).format('DD MMM YYYY, HH:mm') : '—');

export const formatAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? `₹${amount.toFixed(2)}` : '—';
};

// ageing is the server's calculator output ({ elapsedDays, targetDays, status }) or null
// for a bucket with no TAT.
export const formatAge = (ageing) => {
    if (!ageing || ageing.elapsedDays === null || ageing.elapsedDays === undefined) {
        return '—';
    }
    const elapsed = Number(ageing.elapsedDays);
    return elapsed < 1 ? `${Math.round(elapsed * 24)}h` : `${elapsed.toFixed(1)}d`;
};

export const ageStatusLabel = (status) => {
    if (status === 'breached') return 'TAT breached';
    if (status === 'at_risk') return 'At risk';
    if (status === 'within') return 'Within TAT';
    return '';
};

export const listRowBadges = (row) => {
    const badges = [];
    if (row.duplicate_of_request_id) {
        badges.push({ key: 'duplicate', label: `Duplicate of #${row.duplicate_of_request_id}`, tone: 'warn' });
    }
    if (row.revoked_after_export) {
        badges.push({ key: 'revoked', label: 'Revoked after export', tone: 'danger' });
    } else if (row.exported_at) {
        badges.push({ key: 'exported', label: 'Exported', tone: 'ok' });
    }
    if (row.is_serviceable === false) {
        badges.push({ key: 'serviceable', label: 'Pincode not serviceable', tone: 'warn' });
    }
    return badges;
};

// The CSV arrives as a string in the JSON body, so the download is built here instead of
// pointing a link at an endpoint that needs the auth header.
export const downloadCsv = (csv, fileName) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName || 'installation-export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};
